import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BASE_URL } from "../../config";
import { getAppTodayIso } from "../../utils/appDateTime";
import {
  buildAllGroupKeys,
  buildClientScheduleDays,
} from "./clientScheduleGridUtils";
import { computeClientScheduleStats } from "./clientScheduleStatsUtils";

function decodeToken() {
  try {
    const token = localStorage.getItem("token");
    if (!token || token === "null" || token === "undefined") return null;
    const payload = token.split(".")[1];
    if (!payload) return null;
    return JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
  } catch {
    return null;
  }
}

function getAuthHeaders() {
  const token =
    localStorage.getItem("authToken") || localStorage.getItem("token");
  if (!token || token === "null" || token === "undefined" || !String(token).trim()) {
    return {};
  }
  return { Authorization: `Bearer ${token}` };
}

function buildQueryParams({
  searchQuery,
  dateFrom,
  dateRange,
  clientIds,
  checkinStatuses,
}) {
  const q = new URLSearchParams();
  if (searchQuery?.trim()) q.set("search", searchQuery.trim());
  if (dateFrom) q.set("dateFrom", dateFrom);
  if (dateRange) q.set("dateRange", String(dateRange));
  if (clientIds?.length) q.set("client_ids", clientIds.join(","));
  if (checkinStatuses?.length) {
    q.set("checkinState", checkinStatuses.join(","));
  }
  return q;
}

export default function useClientSchedule() {
  const tokenPayload = useMemo(() => decodeToken(), []);
  const userType = String(tokenPayload?.type || "").toLowerCase();
  const tokenClientId = tokenPayload?.client_id
    ? String(tokenPayload.client_id)
    : "";
  const isContactUser = userType === "contact" || userType === "supervisor";

  const [searchQuery, setSearchQuery] = useState("");
  const [dateFrom, setDateFrom] = useState(getAppTodayIso);
  const [dateRange, setDateRange] = useState("7");
  const [clientIds, setClientIds] = useState(() => {
    if (tokenClientId) return [String(tokenClientId)];
    return [];
  });
  const [checkinStatuses, setCheckinStatuses] = useState([]);
  const [clients, setClients] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedCards, setExpandedCards] = useState({});
  const [allExpanded, setAllExpanded] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);
  const autoSelectDoneRef = useRef(false);

  const effectiveClientIds = useMemo(() => {
    if (isContactUser) return clientIds.map(String).filter(Boolean);
    if (tokenClientId) return [String(tokenClientId)];
    const stored = localStorage.getItem("clientId");
    if (stored && stored !== "null") return [stored];
    return [];
  }, [clientIds, isContactUser, tokenClientId]);

  const loadClients = useCallback(async () => {
    if (!isContactUser) return;
    try {
      const res = await fetch(
        `${BASE_URL}/api/contact/assigned-clients?search=`,
        { credentials: "include", headers: { ...getAuthHeaders() } },
      );
      const json = await res.json();
      const list = (json.data || []).map((c) => ({
        id: c.id ?? c.clientId,
        name: c.name ?? c.clientName,
      }));
      setClients(list);
      if (!autoSelectDoneRef.current && list.length === 1) {
        autoSelectDoneRef.current = true;
        setClientIds([String(list[0].id)]);
      }
    } catch {
      setClients([]);
    }
  }, [isContactUser]);

  const fetchData = useCallback(async () => {
    if (!effectiveClientIds.length) {
      setShifts([]);
      return;
    }

    setLoading(true);
    try {
      const params = buildQueryParams({
        searchQuery,
        dateFrom,
        dateRange,
        clientIds: effectiveClientIds,
        checkinStatuses,
      });

      const gridRes = await fetch(
        `${BASE_URL}/api/client/schedule?${params.toString()}`,
        {
          credentials: "include",
          headers: { ...getAuthHeaders() },
        },
      );

      const gridJson = await gridRes.json();
      const rows = gridJson.data || [];
      setShifts(rows);
    } catch {
      setShifts([]);
    } finally {
      setLoading(false);
    }
  }, [
    effectiveClientIds,
    searchQuery,
    dateFrom,
    dateRange,
    checkinStatuses,
  ]);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  useEffect(() => {
    const t = setTimeout(() => {
      fetchData();
    }, searchQuery ? 350 : 0);
    return () => clearTimeout(t);
  }, [fetchData, refreshTick, searchQuery]);

  const scheduleDays = useMemo(() => {
    const days = buildClientScheduleDays(shifts, dateFrom, Number(dateRange) || 7);
    const q = searchQuery.trim().toLowerCase();
    if (!q) return days;
    return days
      .map((day) => ({
        ...day,
        groups: (day.groups || []).filter((g) => {
          const hay = [
            g.clientName,
            g.locationName,
            ...(g.shifts || []).flatMap((s) => [
              s.position_name,
              String(s.shift_id),
              ...(s.assignments || []).map((a) => a.name),
            ]),
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
          return hay.includes(q);
        }),
      }))
      .filter((day) => day.groups.length > 0);
  }, [shifts, dateFrom, dateRange, searchQuery]);

  const stats = useMemo(() => {
    const visibleShifts = (scheduleDays || []).flatMap((day) =>
      (day.groups || []).flatMap((group) => group.shifts || []),
    );
    return computeClientScheduleStats(visibleShifts);
  }, [scheduleDays]);

  const toggleExpandAll = useCallback(() => {
    setAllExpanded((prev) => {
      const next = !prev;
      if (next) {
        setExpandedCards(buildAllGroupKeys(scheduleDays));
      } else {
        setExpandedCards({});
      }
      return next;
    });
  }, [scheduleDays]);

  const toggleCard = useCallback((cardKey) => {
    setExpandedCards((prev) => {
      const next = { ...prev };
      if (next[cardKey]) delete next[cardKey];
      else next[cardKey] = true;
      return next;
    });
  }, []);

  const refresh = useCallback(() => {
    setRefreshTick((v) => v + 1);
  }, []);

  const clearFilters = useCallback(() => {
    setSearchQuery("");
    setDateFrom(getAppTodayIso());
    setDateRange("7");
    setCheckinStatuses([]);
    if (isContactUser) {
      setClientIds(clients.length === 1 ? [String(clients[0].id)] : []);
    }
  }, [clients, isContactUser]);

  const removeFilterChip = useCallback(
    (chip) => {
      const key = chip?.key;
      if (key === "client") setClientIds([]);
      if (key === "date") setDateFrom(getAppTodayIso());
      if (key === "range") setDateRange("7");
      if (key === "checkin" && chip?.value) {
        setCheckinStatuses((prev) => prev.filter((v) => v !== chip.value));
      }
    },
    [],
  );

  return {
    searchQuery,
    setSearchQuery,
    dateFrom,
    setDateFrom,
    dateRange,
    setDateRange,
    clientIds,
    setClientIds,
    checkinStatuses,
    setCheckinStatuses,
    clients,
    shifts,
    stats,
    loading,
    scheduleDays,
    expandedCards,
    allExpanded,
    toggleExpandAll,
    toggleCard,
    refresh,
    clearFilters,
    removeFilterChip,
    isContactUser,
    effectiveClientIds,
    needsClientSelection: isContactUser && effectiveClientIds.length === 0,
  };
}
